import React from "react";
import { Link, useLocation } from "wouter";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  LayoutDashboard,
  Store,
  CreditCard,
  FileText,
  LogOut,
  User,
  Menu,
  X,
} from "lucide-react";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { useState } from "react";

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export function AdminLayout({ children, title }: AdminLayoutProps) {
  const [location] = useLocation();
  const { admin, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/shops", label: "Negocios", icon: Store },
    { path: "/admin/payments", label: "Pagos", icon: CreditCard },
    { path: "/admin/logs", label: "Logs", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con botón hamburguesa */}
      <header className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Panel Admin</h1>
            <p className="text-xs text-gray-500">Sistema de gestión</p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="border-gray-300"
          >
            {sidebarOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </header>

      {/* Sidebar - Desktop */}
      <aside
        className={`fixed right-0 top-0 h-full w-64 bg-white border-l border-gray-200 p-6 transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "translate-x-full"
        } lg:translate-x-0 z-40`}
      >
        <div className="mb-8 pt-2">
          {" "}
          {/* Agregar pt-2 para más espacio arriba */}
          <h1 className="text-xl font-bold text-gray-900 flex items-center">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-2">
              <span className="text-white font-bold">A</span>
            </div>
            Panel Admin
          </h1>
          <p className="text-sm text-gray-500 mt-2">Sistema de gestión</p>{" "}
          {/* Cambiar mt-1 a mt-2 */}
        </div>

        {/* User info */}
        <Card className="p-4 mb-6 bg-gray-50">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-sm">{admin?.fullName}</p>
              <p className="text-xs text-gray-500">{admin?.username}</p>
              <p className="text-xs text-blue-600 font-medium mt-1">
                Super Admin
              </p>
            </div>
          </div>
        </Card>

        {/* Navigation */}
        <nav className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                location === item.path
                  ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600"
                  : "text-gray-700 hover:bg-gray-100 border-l-4 border-transparent"
              }`}
            >
              <item.icon className="h-4 w-4 mr-3" />
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Logout button */}
        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="outline"
            onClick={logout}
            className="w-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:mr-64 pt-16 lg:pt-0 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {title || "Dashboard"}
                </h1>
                <p className="text-gray-600 mt-1">
                  Panel de administración del sistema
                </p>
              </div>
              <div className="hidden lg:flex items-center space-x-2 text-sm text-gray-500">
                <span>Admin:</span>
                <span className="font-medium">{admin?.username}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          {children}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
